import { c as _c } from "react/compiler-runtime";
import { cn } from '@eversports/klimt-design-components/tailwind/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
const buttonVariants = cva('ring-offset-surface-background focus-visible:ring-border-divider rounded-md py-0.5 font-medium inline-flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50', {
  variants: {
    variant: {
      primary: 'bg-surface-brand-primary hover:bg-surface-brand-hover disabled:bg-surface-disabled disabled:text-text-disabled text-white disabled:opacity-100',
      secondary: 'border-border-brand-primary text-text-brand-primary hover:bg-surface-brand-primary-light hover:text-text-hover bg-surface-white border',
      ghost: 'hover:bg-surface-neutral hover:text-text-primary',
      text: 'text-text-brand-primary hover:text-text-hover underline underline-offset-4',
      tertiary: 'border-border-divider text-text-secondary bg-surface-white hover:bg-surface-neutral hover:border-border-enabled border',
      'tertiary-selected': 'border-border-active bg-surface-neutral text-text-selected text-text-primary border',
      'danger-primary': 'bg-surface-error hover:bg-surface-error-hover text-white',
      'danger-tertiary': 'border-border-error text-text-error bg-surface-primary hover:bg-surface-error-disabled hover:border-border-error-hover hover:text-text-error-hover border'
    },
    size: {
      lg: 'px-4 text-lg min-h-[44px]',
      sm: 'min-h-4 px-2 text-base',
      // TODO: Remove after codemod migration - these are aliases for backwards compatibility
      default: 'px-4 text-base min-h-[44px]',
      md: 'px-4 text-base min-h-[44px]',
      icon: 'px-2 text-sm'
    },
    width: {
      auto: '',
      fit: 'w-fit',
      full: 'w-full',
      responsive: 'md:w-fit w-full'
    }
  },
  defaultVariants: {
    variant: 'primary',
    size: 'lg',
    width: 'auto'
  }
});
export interface ButtonProps extends React.AriaAttributes, VariantProps<typeof buttonVariants> {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  type?: 'submit' | 'reset' | 'button';
  form?: string;
  className?: string;
  id?: string;
  tabIndex?: number;
  autoFocus?: boolean;
  role?: React.AriaRole;
  children?: React.ReactNode;
  asChild?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  text?: string;
  [key: `data-${string}`]: string | undefined;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((t0, ref) => {
  const $ = _c(31);
  let children;
  let className;
  let icon;
  let iconPosition;
  let props;
  let size;
  let t1;
  let text;
  let variant;
  let width;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      width,
      icon,
      iconPosition,
      asChild: t1,
      text,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = iconPosition;
    $[5] = props;
    $[6] = size;
    $[7] = t1;
    $[8] = text;
    $[9] = variant;
    $[10] = width;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    iconPosition = $[4];
    props = $[5];
    size = $[6];
    t1 = $[7];
    text = $[8];
    variant = $[9];
    width = $[10];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = (asChild ? Slot : "button") as React.ElementType;
  const hasIcon = Boolean(icon);
  const content = text ?? children;
  let t2;
  if ($[11] !== hasIcon || $[12] !== icon) {
    t2 = hasIcon ? <span className="size-1.5 flex shrink-0 items-center justify-center [&>svg]:size-full">{icon}</span> : null;
    $[11] = hasIcon;
    $[12] = icon;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const iconElement = t2;
  let t3;
  if ($[14] !== className || $[15] !== hasIcon || $[16] !== size || $[17] !== variant || $[18] !== width) {
    t3 = cn(buttonVariants({
      variant,
      size,
      width,
      className
    }), hasIcon && "gap-1");
    $[14] = className;
    $[15] = hasIcon;
    $[16] = size;
    $[17] = variant;
    $[18] = width;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== asChild || $[21] !== content || $[22] !== iconElement || $[23] !== iconPosition) {
    t4 = asChild ? content : <>{iconElement && iconPosition !== "right" && iconElement}{content}{iconElement && iconPosition === "right" && iconElement}</>;
    $[20] = asChild;
    $[21] = content;
    $[22] = iconElement;
    $[23] = iconPosition;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== Comp || $[26] !== props || $[27] !== ref || $[28] !== t3 || $[29] !== t4) {
    t5 = <Comp className={t3} ref={ref} {...props}>{t4}</Comp>;
    $[25] = Comp;
    $[26] = props;
    $[27] = ref;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  return t5;
});
Button.displayName = 'Button';
export { Button, buttonVariants };